/**
 * Scope Resolution Service
 *
 * Pure logic — no React, no Firebase. Takes user + org chart positions → resolved scope.
 *
 * Scope Levels (org-based):
 *   assignments → classes directly on my org chart positions
 *   scope       → my positions + all descendant positions' classes
 *   school      → everything (admin/dev only)
 *
 * Source of truth: Organisation chart positions (staffCards → classCards).
 * class.teacherIds is NOT used for scope (kept for timetable only).
 *
 * Access Types:
 *   default   → landing view when user opens the app
 *   available → user can switch to this level, but it's not the landing
 *   hidden    → level is not shown at all
 */

import { resolveOrgScope } from './organisationScope';
export { resolveOrgScope } from './organisationScope';

// Default scope settings per role
const DEFAULT_SCOPE_SETTINGS = {
  dev:     { assignments: 'available', scope: 'available', school: 'default' },
  admin:   { assignments: 'available', scope: 'available', school: 'default' },
  teacher: { assignments: 'available', scope: 'default',   school: 'available' },
  parent:  { assignments: 'hidden',    scope: 'default',   school: 'hidden' },
};

export const getDefaultScopeSettings = (role) => {
  return DEFAULT_SCOPE_SETTINGS[role] || DEFAULT_SCOPE_SETTINGS.teacher;
};

/**
 * Resolve a user's complete scope.
 */
export const resolveUserScope = ({ user, role, allClasses, allPositions, scopeSettings }) => {
  const settings = scopeSettings || getDefaultScopeSettings(role);
  const classes = allClasses || [];
  const userId = user?.uid;

  const { assignmentClassIds, scopeClassIds } = resolveOrgScope(userId, allPositions || [], classes);

  const schoolClassIds = new Set(classes.map(cls => cls.id));

  const availableLevels = [];
  if (settings.assignments !== 'hidden') availableLevels.push('assignments');
  if (settings.scope !== 'hidden') availableLevels.push('scope');
  if (settings.school !== 'hidden') availableLevels.push('school');

  const defaultLevel =
    settings.school === 'default' ? 'school' :
    settings.scope === 'default' ? 'scope' :
    settings.assignments === 'default' ? 'assignments' :
    availableLevels[0] || 'assignments';

  return {
    settings,
    assignments: { classIds: assignmentClassIds },
    scope: { classIds: scopeClassIds },
    school: { classIds: schoolClassIds },
    defaultLevel,
    availableLevels,
  };
};

export const getClassIdsForLevel = (scope, level) => {
  if (!scope) return new Set();
  switch (level) {
    case 'assignments': return scope.assignments?.classIds || new Set();
    case 'scope':       return scope.scope?.classIds || new Set();
    case 'school':      return scope.school?.classIds || new Set();
    case 'personal':    return scope.assignments?.classIds || new Set();
    case 'grade':       return scope.scope?.classIds || new Set();
    default:            return scope.assignments?.classIds || new Set();
  }
};

export const filterByScope = (items, scope, activeLevel, matchField = 'classId') => {
  if (!items || !scope || !activeLevel) return items || [];
  if (activeLevel === 'school') return items;
  const classIds = getClassIdsForLevel(scope, activeLevel);
  if (classIds.size === 0) return [];
  return items.filter(item => {
    const value = item[matchField];
    if (!value) return false;
    return classIds.has(value);
  });
};

export const isClassInScope = (scope, level, classId) => {
  if (!scope || !classId) return false;
  const classIds = getClassIdsForLevel(scope, level);
  return classIds.has(classId);
};

export const getScopeCounts = (scope, students = [], classes = []) => {
  if (!scope) return {};
  const countForLevel = (level) => {
    const classIds = getClassIdsForLevel(scope, level);
    return {
      classes: classIds.size,
      students: students.filter(s => s.classId && classIds.has(s.classId)).length,
    };
  };
  return {
    assignments: countForLevel('assignments'),
    scope: countForLevel('scope'),
    school: countForLevel('school'),
  };
};
